import { Fragment } from 'react';

import { Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import { EntityField } from '../EntityField';
import { Icon } from '../Icon';
import styles from './EntityCard.module.scss';
import { areaIcons } from '@/constants/areaIcons';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { uiIcons } from '@/constants/uiIcons';
import type { HoverCardField, HoverCardFieldItem } from '@/lib/resource-config';

interface EntityCardProps {
  type?: 'employees' | 'customers' | 'projects';
  id?: string | number;
  fields?: HoverCardFieldItem[];
  loading?: boolean;
  error?: boolean;
}

const isVisible = (field: HoverCardField) => {
  const { value, hideIfEmpty, label } = field;
  if (label === undefined) return false;
  return !(
    value === null ||
    value === undefined ||
    (value === '' && hideIfEmpty)
  );
};

export const EntityCard = ({
  type,
  fields,
  id,
  loading,
  error,
}: EntityCardProps) => {
  const { t } = useTranslation('ui');

  const iconName =
    type === 'employees'
      ? areaIcons.employees
      : type === 'customers'
        ? areaIcons.customers
        : type === 'projects'
          ? areaIcons.projects
          : undefined;

  const generatedUrl =
    type === 'employees' && id
      ? legacyRoutes.employees.details(id)
      : type === 'customers' && id
        ? legacyRoutes.customers.details(id)
        : type === 'projects' && id
          ? legacyRoutes.projectManagement.index(id)
          : undefined;

  const flatFields = fields?.flatMap((item) =>
    item.type === 'row' ? item.fields : [item],
  );

  const name =
    flatFields?.find((f) => f.field?.toLowerCase() === 'name')?.value ?? '—';
  const shortId =
    flatFields?.find(
      (f) =>
        f.field?.toLowerCase() === 'initials' ||
        f.field?.toLowerCase() === 'number',
    )?.value ?? '—';
  const subtitleFieldName = type === 'projects' ? 'customername' : 'position';
  const subtitle = flatFields?.find(
    (f) => f.field?.toLowerCase() === subtitleFieldName,
  )?.value;

  const headerFields = new Set([
    'name',
    'initials',
    'number',
    'position',
    'customername',
  ]);
  const bodyFields = fields?.reduce<HoverCardFieldItem[]>((acc, item) => {
    if (item.type === 'row') {
      const filtered = item.fields.filter(
        (f) => !headerFields.has(f.field?.toLowerCase() ?? ''),
      );
      if (filtered.length) acc.push({ ...item, fields: filtered });
    } else if (!headerFields.has(item.field?.toLowerCase() ?? '')) {
      acc.push(item);
    }
    return acc;
  }, []);

  if (loading) {
    return (
      <Paper elevation={4} className={styles.Wrapper}>
        <Stack>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="70%" height={20} />
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className={styles.Wrapper} elevation={4}>
        <Typography variant="bodyRegular" color="error">
          {t('entityCard.FailedToLoadDetails')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className={styles.Wrapper} elevation={4}>
      <Stack gap={2}>
        <Stack direction="row" alignItems="center">
          {iconName && (
            <Icon
              name={iconName}
              color="secondary"
              backgroundColor="lightGray"
            />
          )}
          <Stack gap={0.25}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography>{name as string}</Typography>
              <Typography variant="label" color="text.disabled">
                ({shortId as string})
              </Typography>
            </Stack>
            {!!subtitle && (
              <Typography variant="label" color="text.secondary">
                {subtitle as string}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack component="dl">
          {bodyFields?.map((item) => {
            if (item.type === 'row') {
              const visibleFields = item.fields.filter(isVisible);
              return (
                <Stack
                  key={item.fields.map((f) => f.field).join('-')}
                  direction="row"
                  gap={2}
                  // Use sx prop due to this issue:
                  // https://github.com/mui/material-ui/issues/47844
                  sx={{ rowGap: 1, columnGap: 2 }}
                  flexWrap="wrap"
                  component="div"
                >
                  {visibleFields.map((field) => (
                    <Fragment key={field.label}>
                      <EntityField field={field} />
                    </Fragment>
                  ))}
                </Stack>
              );
            }
            return <EntityField key={item.label} field={item} />;
          })}
        </Stack>

        {generatedUrl && (
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              size="tiny"
              component={RouterLink}
              to={generatedUrl}
              endIcon={<Icon name={uiIcons.navigation.externalLink} />}
              target="_blank"
            >
              {type === 'employees'
                ? t('entityCard.ViewEmployee')
                : type === 'projects'
                  ? t('entityCard.ViewProject')
                  : t('entityCard.ViewCustomer')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
